import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { Checkbox } from "react-native-paper";
import { DatePickerInput } from "react-native-paper-dates";

import { useToast } from "../../../../contexts/Toast/ToastProvider";
import { formatDateWithTime, isSubmissionLate } from "../../../../utils/date";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import QuestionCard from "./QuestionCard";
import McqQuestionCard from "./McqQuestionCard";
import { colors } from "../../../../styles/design-system";
import {
  useGetAssessmentSubmissionsDetailsQuery,
  UpdatedEntityType,
  AssessmentType,
} from "@axio/graphql/generated/graphql";
import { useGradingCenterSubmissions } from "../../../../contexts/GradingCenterContext";
import type { GradeChangeParams, GradeTree } from "./types";

const DatePickerInputComponent = DatePickerInput;

const SubmissionGrading: React.FC = () => {
  const {
    selectedSubmissionData,
    setUpdatedAssessmentGradeEntry,
    updatedAssessmentGradeEntry,
    setCanSaveAssessment,
    updatedAssessmentRetakePayload,
    setUpdatedAssessmentRetakePayload,
  } = useGradingCenterSubmissions();

  const { showToast } = useToast();

  const [gradeTree, setGradeTree] = useState<GradeTree | null>(null);
  const [assessmentRetakeAllowed, setAssessmentRetakeAllowed] = useState(false);
  const [assessmentRetakeDeadline, setAssessmentRetakeDeadline] = useState<Date | null>(null);

  const [activelyEditedGradeElement, setActivelyEditedGradeElement] = useState<string>("");
  const previousGradeValuesRef = useRef<Record<string, string>>({});

  const { data: assessmentSubmissionDetails, loading: assessmentSubmissionDetailsLoading } =
    useGetAssessmentSubmissionsDetailsQuery({
      variables: {
        assessmentSubmissionId: selectedSubmissionData.id,
        studentId: selectedSubmissionData.student?.id,
      },
      skip: !selectedSubmissionData?.id || !selectedSubmissionData?.student?.id,
    });

  const detailedAssessmentSubmission = assessmentSubmissionDetails?.getAssessmentSubmissionDetails?.data;
  const isMcq = detailedAssessmentSubmission?.assessmentType === AssessmentType.Mcq;
  const manualOverrideActive = detailedAssessmentSubmission?.manualOverrideActive === true;

  const dueDate = selectedSubmissionData?.dueDate;
  const submittedAt = detailedAssessmentSubmission?.completedAt || detailedAssessmentSubmission?.created || "";
  const isLate =
    dueDate &&
    submittedAt &&
    isSubmissionLate({
      submittedDate: submittedAt,
      dueDate,
      retakeDeadline: selectedSubmissionData?.retakeDeadline,
      retakeDeadlineSnapshot: selectedSubmissionData?.retakeDeadlineSnapshot,
    });
  const submittedTimingText = dueDate ? (isLate ? "Late submission" : "On time") : null;
  const formattedDueDate = dueDate
    ? new Date(dueDate).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : "No due date";

  useEffect(() => {
    if (detailedAssessmentSubmission) {
      setGradeTree({
        id: detailedAssessmentSubmission?.id,
        totalPoints: detailedAssessmentSubmission?.totalPoints?.toString() ?? "0",
        aiTotalPoints: detailedAssessmentSubmission?.aiTotalPoints?.toString() ?? "0",
        maximumPoints: detailedAssessmentSubmission?.maximumPoints ?? 0,
        questions: isMcq
          ? []
          : detailedAssessmentSubmission?.questions?.map((question) => ({
              id: question.id,
              totalPoints: question.userAnswer.totalPoints?.toString() ?? "0",
              aiTotalPoints: question.userAnswer.aiTotalPoints?.toString() ?? "0",
              maximumPoints: question.userAnswer.maximumPoints ?? 0,
              rubrics: question.rubrics.map((rubric) => ({
                id: rubric.id,
                pointsAwarded: rubric.pointsAwarded?.toString() ?? "0",
                aiPointsAwarded: rubric.aiPointsAwarded?.toString() ?? "0",
                maxPoints: rubric.maxPoints ?? 0,
              })),
            })),
      });
    }
  }, [detailedAssessmentSubmission]);

  useEffect(() => {
    if (detailedAssessmentSubmission) {
      setUpdatedAssessmentGradeEntry({
        updatedEntityType: null,
        updatedEntityId: null,
        updatedGrade: "",
        updatedFeedback: detailedAssessmentSubmission.feedback,
      });
      setAssessmentRetakeAllowed(detailedAssessmentSubmission.retakeAllowed === true);
      setAssessmentRetakeDeadline(
        detailedAssessmentSubmission.retakeDeadline ? new Date(detailedAssessmentSubmission.retakeDeadline) : null
      );
      setUpdatedAssessmentRetakePayload(null);
    }
  }, [assessmentSubmissionDetails]);

  const isSelectedSubmissionNeedsReview =
    selectedSubmissionData?.status === "NEEDS_REVIEW" || selectedSubmissionData?.status === "Needs Review";

  const hasValidFutureRetakeDeadline =
    assessmentRetakeDeadline instanceof Date &&
    !Number.isNaN(assessmentRetakeDeadline.getTime()) &&
    assessmentRetakeDeadline.getTime() > Date.now();

  useEffect(() => {
    const hasGradeEntry =
      (updatedAssessmentGradeEntry?.updatedGrade ?? "") !== "" ||
      (isSelectedSubmissionNeedsReview && selectedSubmissionData?.grade != null);
    const hasGradeChange = !!updatedAssessmentGradeEntry?.updatedEntityId || isSelectedSubmissionNeedsReview;
    const hasRetakeChange = updatedAssessmentRetakePayload
      ? updatedAssessmentRetakePayload.retakeAllowed === true
        ? !assessmentRetakeDeadline || hasValidFutureRetakeDeadline
        : true
      : false;

    setCanSaveAssessment((hasGradeEntry && hasGradeChange) || hasRetakeChange);
  }, [
    updatedAssessmentGradeEntry,
    isSelectedSubmissionNeedsReview,
    selectedSubmissionData?.grade,
    assessmentRetakeAllowed,
    assessmentRetakeDeadline,
    hasValidFutureRetakeDeadline,
    updatedAssessmentRetakePayload,
    setCanSaveAssessment,
  ]);

  // Calculate time taken
  const getTimeTaken = () => {
    if (detailedAssessmentSubmission?.startedAt && detailedAssessmentSubmission?.completedAt) {
      const start = new Date(detailedAssessmentSubmission.startedAt);
      const end = new Date(detailedAssessmentSubmission.completedAt);
      const diffMs = end.getTime() - start.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      return `${diffMins} min`;
    }
    return "0 min";
  };

  const handleGradeChange = async (input: GradeChangeParams) => {
    const { updatedEntityType, updatedEntityId, updatedGrade, maxPointsForEntity } = input;

    if (updatedGrade.trim() === "") {
      if (updatedEntityType === UpdatedEntityType.Submission) {
        setGradeTree({
          ...gradeTree,
          totalPoints: "",
        });
      } else if (updatedEntityType === UpdatedEntityType.Question) {
        setGradeTree({
          ...gradeTree,
          questions: gradeTree.questions.map((question) =>
            question.id === updatedEntityId ? { ...question, totalPoints: "" } : question
          ),
        });
      } else if (updatedEntityType === UpdatedEntityType.Rubric) {
        const q = gradeTree.questions.find((question) => question.rubrics.some((r) => r.id === updatedEntityId));

        if (!q) {
          return;
        }

        setGradeTree({
          ...gradeTree,
          questions: gradeTree.questions.map((question) => {
            if (question.id === q.id) {
              return {
                ...question,
                rubrics: question.rubrics.map((r) => (r.id === updatedEntityId ? { ...r, pointsAwarded: "" } : r)),
              };
            }
            return question;
          }),
        });
      }
      return;
    }

    // trim all non-numeric characters
    let sanitizedUpdatedGrade = updatedGrade.replace(",", ".");
    sanitizedUpdatedGrade = sanitizedUpdatedGrade.replace(/[^0-9.]/g, "");

    const numberUpdatedGrade = sanitizedUpdatedGrade;

    if (Number.isNaN(Number(numberUpdatedGrade))) {
      showToast({
        message: "Invalid score. Please enter a valid number",
        type: "error",
      });
      return;
    }

    const clampedGrade = Math.min(Math.max(Number(numberUpdatedGrade), 0), maxPointsForEntity).toString();

    if (updatedEntityType === UpdatedEntityType.Submission) {
      setGradeTree({
        ...gradeTree,
        totalPoints: clampedGrade,
      });
    } else if (updatedEntityType === UpdatedEntityType.Question) {
      setGradeTree({
        ...gradeTree,
        questions: gradeTree.questions.map((question) =>
          question.id === updatedEntityId ? { ...question, totalPoints: clampedGrade } : question
        ),
      });
    } else if (updatedEntityType === UpdatedEntityType.Rubric) {
      const q = gradeTree.questions.find((question) => question.rubrics.some((r) => r.id === updatedEntityId));

      if (!q) {
        return;
      }

      setGradeTree({
        ...gradeTree,
        questions: gradeTree.questions.map((question) => {
          if (question.id === q.id) {
            return {
              ...question,
              rubrics: question.rubrics.map((r) =>
                r.id === updatedEntityId ? { ...r, pointsAwarded: clampedGrade } : r
              ),
            };
          }

          return question;
        }),
      });
    }

    setUpdatedAssessmentGradeEntry({
      updatedEntityType,
      updatedEntityId,
      updatedGrade: clampedGrade,
    });
  };

  const handleStartEdit = (elementId: string, currentValue: string) => {
    previousGradeValuesRef.current[elementId] = currentValue;
    setActivelyEditedGradeElement(elementId);
  };

  const handleGradeBlur = (input: {
    updatedEntityType: UpdatedEntityType;
    updatedEntityId: string;
    maxPointsForEntity: number;
  }) => {
    const { updatedEntityType, updatedEntityId, maxPointsForEntity } = input;
    setActivelyEditedGradeElement("");

    if (!gradeTree) {
      return;
    }

    let currentValue = "";

    if (updatedEntityType === UpdatedEntityType.Submission) {
      currentValue = gradeTree.totalPoints ?? "";
    } else if (updatedEntityType === UpdatedEntityType.Question) {
      currentValue = gradeTree.questions.find((question) => question.id === updatedEntityId)?.totalPoints ?? "";
    } else if (updatedEntityType === UpdatedEntityType.Rubric) {
      const question = gradeTree.questions.find((q) => q.rubrics.some((rubric) => rubric.id === updatedEntityId));
      currentValue = question?.rubrics.find((rubric) => rubric.id === updatedEntityId)?.pointsAwarded ?? "";
    }

    if (currentValue.trim() !== "") {
      return;
    }

    const fallbackValue = previousGradeValuesRef.current[updatedEntityId];
    if (!fallbackValue || fallbackValue.trim() === "") {
      return;
    }

    handleGradeChange({
      updatedEntityType,
      updatedEntityId,
      updatedGrade: fallbackValue,
      maxPointsForEntity,
    });
  };

  return (
    <View style={styles.assessmentTabContent}>
      {assessmentSubmissionDetailsLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#3E68FE" />
        </View>
      ) : (
        <>
          {/* Student Info */}
          <View style={styles.studentAssessmentInfo}>
            <View style={styles.studentDetails}>
              <View style={styles.studentAvatar}>
                <CustomText
                  text={selectedSubmissionData?.student?.initials}
                  size="s"
                  weight="bold"
                  style={styles.avatarText}
                  useTranslationText={false}
                />
              </View>
              <View style={styles.studentInfo}>
                <CustomText
                  text={selectedSubmissionData?.student?.name}
                  size="s"
                  weight="bold"
                  numberOfLines={1}
                  useTranslationText={false}
                />
                <CustomText
                  text={selectedSubmissionData?.student?.email}
                  size="xs"
                  style={styles.email}
                  numberOfLines={1}
                  useTranslationText={false}
                />
              </View>
            </View>
            <View style={styles.assessmentDetails}>
              <View style={styles.assessmentDetail}>
                <CustomText text="Due Date" size="xs" style={styles.assessmentDetailText} />
                <CustomText text={formattedDueDate} size="xs" weight="bold" useTranslationText={false} />
              </View>
              <View style={styles.assessmentDetail}>
                <CustomText text="submitted" size="xs" style={styles.assessmentDetailText} />
                <CustomText text={formatDateWithTime(submittedAt)} size="xs" weight="bold" useTranslationText={false} />
              </View>
              <View style={styles.assessmentDetail}>
                <CustomText text="time_spent" size="xs" style={styles.assessmentDetailText} />
                <CustomText text={getTimeTaken()} size="xs" weight="bold" useTranslationText={false} />
                {detailedAssessmentSubmission?.timeLimit != null && detailedAssessmentSubmission.timeLimit > 0 && (
                  <CustomText
                    text="out_of"
                    size="xs"
                    interpolation={{ timeLimit: detailedAssessmentSubmission.timeLimit }}
                    style={[styles.assessmentDetailText, styles.assessmentDetailLimit]}
                  />
                )}
              </View>
              {submittedTimingText && (
                <View style={styles.timingTagRow}>
                  <View style={[styles.timingTag, isLate ? styles.timingTagLate : styles.timingTagOnTime]}>
                    <CustomText
                      text={submittedTimingText}
                      size="xs"
                      style={[styles.timingTagText, isLate ? styles.timingTagTextLate : styles.timingTagTextOnTime]}
                      useTranslationText={false}
                    />
                  </View>
                </View>
              )}
            </View>
          </View>

          {/* Assessment Score Section */}
          <View style={styles.assessmentScoreSection}>
            <CustomText text="Assessment Score" size="l" weight="bold" style={styles.sectionTitle} />

            <View style={styles.scoreSummary}>
              <View style={styles.aiScoreDisplay}>
                <CustomText
                  text={isMcq ? "Auto Score" : "AI-Generated Score"}
                  weight="normal"
                  style={styles.scoreLabel}
                  useTranslationText={false}
                />
                <CustomText
                  text={`${gradeTree?.aiTotalPoints ?? "–"}/${gradeTree?.maximumPoints ?? "–"}`}
                  size="xxl"
                  weight="bold"
                  style={styles.scoreValue}
                  useTranslationText={false}
                />
                {isMcq && gradeTree?.maximumPoints > 0 && (
                  <CustomText
                    text={`(${Math.round((Number(gradeTree?.aiTotalPoints) / gradeTree.maximumPoints) * 100)}%)`}
                    size="s"
                    style={styles.scorePercentage}
                    useTranslationText={false}
                  />
                )}
              </View>

              <View style={styles.overrideScoreContainer}>
                <View style={styles.overrideLabelRow}>
                  <CustomText text="Final Score (Override)" size="s" style={[styles.label, styles.overrideLabel]} />
                  {manualOverrideActive && (
                    <View style={styles.manualOverrideBadge}>
                      <CustomText
                        text="Manual override applied"
                        size="xs"
                        style={styles.manualOverrideBadgeText}
                        useTranslationText={false}
                      />
                    </View>
                  )}
                </View>
                <View style={styles.scoreAndRetakeRow}>
                  <View style={styles.scoreInputWrapper}>
                    <CustomTextInput
                      onFocus={() => handleStartEdit(gradeTree?.id, gradeTree?.totalPoints ?? "")}
                      value={gradeTree?.totalPoints}
                      onChangeText={(text) =>
                        handleGradeChange({
                          updatedEntityType: UpdatedEntityType.Submission,
                          updatedEntityId: gradeTree.id,
                          updatedGrade: text,
                          maxPointsForEntity: gradeTree.maximumPoints ?? 0,
                        })
                      }
                      placeholder=""
                      keyboardType="numeric"
                      style={styles.scoreInput}
                      onBlur={() =>
                        handleGradeBlur({
                          updatedEntityType: UpdatedEntityType.Submission,
                          updatedEntityId: gradeTree?.id || "",
                          maxPointsForEntity: gradeTree?.maximumPoints ?? 0,
                        })
                      }
                    />
                    <CustomText
                      text={gradeTree?.maximumPoints != null ? `/${gradeTree.maximumPoints}` : ""}
                      weight="bold"
                      useTranslationText={false}
                    />
                  </View>
                  <View style={styles.checkboxContainer}>
                    <Checkbox
                      status={assessmentRetakeAllowed ? "checked" : "unchecked"}
                      onPress={() => {
                        const nextAllowed = !assessmentRetakeAllowed;
                        const nextDeadline = nextAllowed ? assessmentRetakeDeadline : null;
                        setAssessmentRetakeAllowed(nextAllowed);
                        setAssessmentRetakeDeadline(nextDeadline);
                        setUpdatedAssessmentRetakePayload({
                          retakeAllowed: nextAllowed,
                          retakeDeadline: nextDeadline,
                        });
                      }}
                      theme={{ colors: { primary: "#3B82F6" } }}
                    />
                    <CustomText text="Allow Retake" size="s" style={styles.checkboxLabel} />
                  </View>
                </View>
                <CustomText
                  text={
                    isMcq
                      ? "Leave blank to use auto score, or enter override value"
                      : "Leave blank to use AI score, or enter a value to override the current score."
                  }
                  size="xs"
                  style={styles.scoreHelp}
                  useTranslationText={false}
                />
                {assessmentRetakeAllowed && (
                  <View style={styles.retakeOptions}>
                    <CustomText text="Retake Deadline" size="s" weight="bold" style={styles.label} />
                    <DatePickerInputComponent
                      locale="en"
                      label={undefined}
                      value={assessmentRetakeDeadline}
                      onChange={(date) => {
                        const normalizedDate = date ?? null;
                        setAssessmentRetakeDeadline(normalizedDate);
                        setUpdatedAssessmentRetakePayload({
                          retakeAllowed: true,
                          retakeDeadline: normalizedDate,
                        });
                      }}
                      inputMode="start"
                      mode="outlined"
                      withDateFormatInLabel={false}
                      outlineColor={"#D1D5DB"}
                      activeOutlineColor={"#3B82F6"}
                      placeholder="MM/DD/YYYY"
                      theme={{ roundness: 8 }}
                      style={styles.dateInput}
                      aria-label="Retake deadline date picker"
                      validRange={{
                        startDate: new Date(),
                      }}
                    />
                  </View>
                )}
              </View>
            </View>
          </View>

          {/* Question Breakdown Section */}
          <View style={styles.questionBreakdownSection}>
            <CustomText text="Question Breakdown" size="l" weight="bold" style={styles.sectionTitle} />
            <View style={styles.questionsList}>
              {isMcq
                ? (detailedAssessmentSubmission?.mcqAnswers ?? []).map((mcqAnswer, index) => (
                    <McqQuestionCard
                      key={mcqAnswer.id}
                      mcqAnswer={mcqAnswer}
                      questionNumber={`Question ${index + 1}`}
                    />
                  ))
                : (detailedAssessmentSubmission?.questions ?? []).map((question, index) => {
                    const gradeTreeQuestion = gradeTree?.questions?.find((q) => q.id === question.id);

                    return (
                      <QuestionCard
                        key={question.id}
                        question={question}
                        gradeTreeQuestion={gradeTreeQuestion}
                        questionNumber={`Question ${index + 1}`}
                        activelyEditedGradeElement={activelyEditedGradeElement}
                        onStartEdit={handleStartEdit}
                        handleGradeChange={handleGradeChange}
                        onGradeBlur={handleGradeBlur}
                      />
                    );
                  })}
            </View>
          </View>

          {/* Overall Feedback Section */}
          <View style={styles.feedbackContainer}>
            <CustomText text="Overall Feedback (Optional)" size="s" style={styles.label} />
            <CustomTextInput
              value={updatedAssessmentGradeEntry?.updatedFeedback}
              onChangeText={(text) =>
                setUpdatedAssessmentGradeEntry({
                  ...updatedAssessmentGradeEntry,
                  updatedEntityType: UpdatedEntityType.Submission,
                  updatedEntityId: gradeTree?.id,
                  updatedGrade: gradeTree?.totalPoints,
                  updatedFeedback: text,
                })
              }
              placeholder="Provide overall feedback on the student's assessment performance..."
              multiline
              numberOfLines={8}
              maxLength={10000}
              style={styles.feedbackTextarea}
            />
            <CustomText
              text="character_count"
              size="xs"
              interpolation={{ value: updatedAssessmentGradeEntry?.updatedFeedback?.length || 0 }}
              style={styles.feedbackCharCount}
            />
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  assessmentTabContent: {
    display: "flex",
  },
  sectionTitle: {
    color: "#374151",
    marginBottom: 12,
  },
  label: {
    marginBottom: 8,
    color: "#344054",
  },
  // Student Assessment Info Styles
  studentAssessmentInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 10,
    padding: 16,
    marginBottom: 24,
  },
  studentDetails: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flex: 1,
    minWidth: 0,
    marginRight: 12,
  },
  studentAvatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: "#3B82F6",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#FFFFFF",
  },
  studentInfo: {
    flex: 1,
    minWidth: 0,
  },
  email: {
    color: "#6B7280",
    marginTop: 2,
  },
  assessmentDetails: {
    alignItems: "flex-end",
    flexShrink: 1,
    minWidth: 0,
  },
  assessmentDetail: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  assessmentDetailText: {
    color: "#475467",
  },
  assessmentDetailLimit: {
    fontStyle: "italic",
  },
  timingTagRow: {
    marginTop: 6,
    alignItems: "flex-end",
  },
  timingTag: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    alignSelf: "flex-start",
    borderWidth: 1,
  },
  timingTagLate: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FCA5A5",
  },
  timingTagOnTime: {
    backgroundColor: "#ECFDF3",
    borderColor: "#A7F3D0",
  },
  timingTagText: {
    fontWeight: "600",
    fontSize: 10,
  },
  timingTagTextLate: {
    color: "#DC2626",
  },
  timingTagTextOnTime: {
    color: "#047857",
  },
  // Assessment Score Section Styles
  assessmentScoreSection: {
    marginBottom: 24,
    alignItems: "flex-start",
  },
  scoreSummary: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 24,
    marginBottom: 20,
  },
  aiScoreDisplay: {
    maxWidth: 300,
    padding: 16,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    backgroundColor: "#F0FDF4",
    borderWidth: 1,
    borderColor: "#BBF7D0",
    borderRadius: 8,
  },
  scoreLabel: {
    color: "#344054",
  },
  scoreValue: {
    color: "#3E68FE",
  },
  scorePercentage: {
    color: colors.grey[500],
  },
  overrideScoreContainer: {
    flex: 1,
  },
  overrideLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  overrideLabel: {
    marginBottom: 0,
  },
  manualOverrideBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: colors.warning[50],
    borderWidth: 1,
    borderColor: colors.warning[200],
  },
  manualOverrideBadgeText: {
    color: colors.warning[700],
    fontWeight: "600",
  },
  scoreInputWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    maxWidth: 110,
  },
  scoreInput: {
    padding: 12,
    paddingRight: 10,
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    fontSize: 16,
    fontWeight: "600",
  },
  scoreHelp: {
    color: "#6B7280",
    marginTop: 6,
  },
  // Question Breakdown Section Styles
  questionBreakdownSection: {
    marginBottom: 24,
  },
  questionsList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  // Feedback Section Styles
  feedbackContainer: {
    flex: 1,
  },
  scoreAndRetakeRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  checkboxContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkboxLabel: {
    color: "#374151",
  },
  retakeOptions: {
    marginTop: 12,
  },
  dateInput: {
    backgroundColor: "#FFFFFF",
    maxHeight: 56,
  },
  feedbackTextarea: {
    marginTop: 8,
    minHeight: 200,
    textAlignVertical: "top",
  },
  feedbackCharCount: {
    color: "#6B7280",
    textAlign: "right",
    marginTop: 6,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
});

export default SubmissionGrading;
